<template>
    <div>
        <div :class="sectionClass"
             :style="searchWrapperStyle">
            <div class="">
                <!-- Mobile page header placeholder: start -->
                <div class="u-trans u-trans-height" :class="{'h-40 md:h-0': !isSearchOverlayScrolledDown, 'h-0 md:h-0': isSearchOverlayScrolledDown}"/>
                <div class="h-60 flex items-center md:hidden w-full">
                    <CultureAwareRouterLink :to="frontpageUrl" :title="logoAltText" @click="clickCloseMobileSearch">
                        <div :class="{ 'text-white': isInWhiteTheme, 'text-blue-500': !isInWhiteTheme }">
                            <template v-if="serverContextService.isOprema">
                                <CIcon name="oprema-logo" original width="150" class="block oprema-logo"/>
                            </template>
                            <template v-else-if="serverContextService.isHesa">
                                <CIcon name="logo-half-top" original width="40" class="block logo-half-top h-40"/>
                                <CIcon name="logo-half-eet" original width="40" class="block logo-half-eet"/>
                            </template>
                            <template v-else-if="serverContextService.isDias">
                                <CIcon name="dias-logo" original width="90" class="block dias-logo h-40"/>
                            </template>
                            <template v-else>
                                <CIcon name="logo-half-top" width="60" class="block logo-half-top"/>
                                <CIcon name="logo-half-eet" width="52" class="block logo-half-eet"/>
                            </template>
                        </div>
                    </CultureAwareRouterLink>
                    <button type="button"
                            class="ml-auto py-5 pr-5"
                            :class="{ 'text-white': isInWhiteTheme, 'theme-text-linkcolor': !isInWhiteTheme }"
                            @click="clickCloseMobileSearch">
                        <CIcon name="close" width="18" height="18"/>
                        <span class="sr-only">{{ $dictionary.get('Generic.Close') }}</span>
                    </button>
                </div>
                <div class="md:hidden text-center font-semi-bold text-15 pb-8" :class="{ 'text-white': isInWhiteTheme }">
                    {{ $dictionary.get('Search.MobileSearchFieldHeader') }}
                </div>
                <!-- Mobile page header placeholder: end -->
                <section role="search"
                         class="input-search relative w-full md:w-auto"
                         :aria-label="$dictionary.get('Accessibility.SkipLinks.PageSearch')">
                    <input id="search-form"
                           ref="inputElemRef"
                           v-skip-target="{ id: 'search-form', label: $dictionary.get('Accessibility.SkipLinks.PageSearch.Label'), order: 3 }"
                           v-prohibit-zoom
                           maxlength="250"
                           name="search"
                           type="search"
                           class="input-field h-full w-full px-20 text-blue-500 oprema:text-black hesa:text-black dias:text-diasblue-500 placeholder-blue-500 oprema:placeholder-black hesa:placeholder-hesared-500 dias:placeholder-diasblue-500 text-12 rounded-full border"
                           :style="searchInputStyle"
                           :placeholder="$dictionary.get('Search.SearchFieldPlaceholder')"
                           @input="lowLevelInputHandler($event)">
                    <transition mode="out-in"
                                enter-active-class="animated fadeIn u-anim-dur-200"
                                leave-active-class="animated fadeOut u-anim-dur-200">
                        <button v-if="term" key="reset" class="reset-icon"
                                type="button"
                                @click="reset">
                            <CIcon name="close" :color="searchInputIconColor"/>
                            <span class="sr-only">{{ $dictionary.get('Generic.Back') }}</span>
                        </button>
                        <CIcon v-else key="search" name="search"
                               class="search-icon"
                               :color="searchInputIconColor"/>
                    </transition>
                </section>
                <div class="u-trans u-trans-height" :class="{'h-40 md:h-0': !isSearchOverlayScrolledDown, 'h-10 md:h-0': isSearchOverlayScrolledDown}"/>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { CSSProperties, StyleValue, ref, computed, watch, PropType, onMounted, onBeforeUnmount, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import debounce from 'lodash-es/debounce';
import urlHelper from '@/core/urlHelper.service';
import themeService from '@/project/app/themes/theme.service';
import serverContextService from '@/core/serverContext.service';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwind from '@/styling/tailwind.config';
const tailwindConfiguration = resolveConfig(tailwind);

const props = defineProps({
    showSearchField: {
        type: Boolean,
        default: false
    },
    sectionStyling: {
        type: Object as PropType<StyleValue>,
        required: false,
        default: null
    },
    sectionClass: {
        type: String,
        default: ''
    }
});

const route = useRoute();

const emit = defineEmits<{
    (e: 'closeMobileSearch'): void;
    (e: 'openMobileSearch'): void;
}>();

const minNoOfCharacters = 2;
const debounceTime = 300;
const debounceSetTerm = debounce(setTerm, debounceTime);
let focusTimeout: ReturnType<typeof setTimeout> | undefined;

const term = ref('');
const showOverlay = ref<boolean>(false);
const typing = ref<boolean>(false);
const isSearchOverlayScrolledDown = ref<boolean>(false);
const inputElemRef = useTemplateRef<HTMLInputElement | null>('inputElemRef');

const searchInputIconColor = computed((): string => {
    if (themeService.isInWhiteTheme) {
        return 'white';
    }

    if (serverContextService?.isOprema) {
        return 'black';
    }

    return serverContextService.isHesa ? tailwindConfiguration.theme.colors.hesagreen['500'] : tailwindConfiguration.theme.colors.blue['500'];
});

const searchInputStyle = computed((): StyleValue => {
    const isInWhiteTheme = themeService.isInWhiteTheme;

    return {
        ...(isInWhiteTheme && { color: '#fff' }),
        ...(isInWhiteTheme && { backgroundColor: 'transparent' }),
        ...(isInWhiteTheme && { borderColor: '#fff' })
    };
});

const searchWrapperStyle = computed((): StyleValue => {
    return {
        ...(props.sectionStyling as CSSProperties || {}),
        transform: `translateY(${props.showSearchField ? '0' : '-200px'})`
    };
});

const isInWhiteTheme = computed(() => {
    return themeService.isInWhiteTheme;
});

const logoAltText = computed(():string => {
    return serverContextService.footer?.companyDetails?.companyName || '';
});

const frontpageUrl = computed(():string => {
    return serverContextService.siteInfo?.url || '/';
});

watch(() => route.path, (newRoute, oldRoute) => {
    if (newRoute !== oldRoute) {
        emit('closeMobileSearch');
    }
});

watch(() => route.query, () => {
    const term = urlHelper.getTerm();
    if (!showOverlay.value) {
        if (!term) {
            clickCloseMobileSearch();
            return;
        } else {
            onTermChanged(term);
            openMobileSearch();
        }
    }

    if (!term) {
        // We came to page without term - reset / close
        setTerm('', false);
    } else {
        // Term updated - for instance from translation (alternative) search
        setSyncedTerm(urlHelper.getTerm()!);
        openMobileSearch();
    }
});

watch(() => term.value, (newTerm: string) => {
    onTermChanged(newTerm);
});

watch(() => props.showSearchField, (showSearchField: boolean) => {
    if (showSearchField) {
        focusSearchInputField();
    }
});

function onTermChanged(term: string) {
    typing.value = true;
    debounceSetTerm(term);
}

function setTerm(term: string, goBack: boolean = true) {
    setSyncedTerm(term);
    const oldShow = showOverlay.value;
    showOverlay.value = shouldShowOverlay(term);
    const justShown = showOverlay.value && !oldShow;

    if (showOverlay.value) {
        urlHelper.setTerm(term, justShown);
        if (!justShown) {
            urlHelper.clearFacets();
        }
    }
    typing.value = false;
}

function reset() {
    setTerm('');
}

function clickCloseMobileSearch() {
    emit('closeMobileSearch');
}

function openMobileSearch() {
    emit('openMobileSearch');
    isSearchOverlayScrolledDown.value = false;
}

function focusSearchInputField() {
    // To focus input field on iOS, here comes a hack:
    // create invisible dummy input to receive the focus first
    const fakeInput = document.createElement('input');
    fakeInput.setAttribute('type', 'text');
    fakeInput.style.position = 'absolute'; // hide fake input field
    fakeInput.style.opacity = '0';
    fakeInput.style.height = '0';
    fakeInput.style.fontSize = '16px'; // disable auto zoom
    document.body.prepend(fakeInput);
    // focus so that subsequent async focus will work
    fakeInput.focus();

    if (focusTimeout) {
        clearTimeout(focusTimeout);
    }
    focusTimeout = setTimeout(() => {
        // now we can focus on the target input
        const targetInput = inputElemRef.value;
        if (targetInput) {
            targetInput.style.fontSize = '16px'; // disable auto zoom
            targetInput.focus();
            targetInput.style.fontSize = 'inherit'; // reset font size
        }
        // cleanup
        fakeInput.remove();
    }, 300);
}

function shouldShowOverlay(term: string): boolean {
    return term?.length >= minNoOfCharacters;
}

function setSyncedTerm(termVar: string = '') {
    term.value = termVar;
    inputElemRef.value!.value = termVar;
}

function lowLevelInputHandler(event) {
    const termVar = event.target.value;
    if (termVar.length > 0 && termVar.trim().length === 0) {
        inputElemRef.value!.value = '';
    } else {
        term.value = termVar;
    }
}

onMounted(() => {
    showOverlay.value = shouldShowOverlay(term.value);
    setSyncedTerm(urlHelper.getTerm());
});

onBeforeUnmount(() => {
    if (focusTimeout) {
        clearTimeout(focusTimeout);
    }
})
</script>

<style lang="less" scoped>
    .input-search {
        transition: height 200ms ease;
        height: 4rem;

        .top-header-fixed & {
            height: 3.2rem;
        }
    }

    .input-field {
        padding-right: 30px;

        &:focus,
        &.touched {
            outline: 0;
        }

        &.border {
            @apply border-blue-500 oprema:border-opremablue-500 hesa:border-hesared-500 dias:border-diasblue-500 border-2;
        }

        &::placeholder {
            color: inherit;
            opacity: 0.8;
        }
    }

    .reset-icon, .search-icon {
        position: absolute;
    }

    .reset-icon {
        right: 19px;
        top: 15px;
        height: 11px;
        width: 11px;

        svg {
            @apply absolute top-0 left-0 h-full w-full;
        }

        .top-header-fixed & {
            top: 11px;
        }

        &:after {
            @import (reference) '../../styling/1-Tools/extend-clickable-area';
            .extend-clickable-area()
        }
    }

    .search-icon {
        @apply text-blue-500 oprema:text-opremablack hesa:text-hesared-500 dias:text-diasblue-500;
        right: 16px;
        top: 13px;
        height: 15px;
        width: 15px;

        .top-header-fixed & {
            top: 9px;
        }
    }
    body[data-theme="oprema"] {
        .input-field {
            padding-bottom: 0.1rem;

            &.border {
                @apply border-black;
            }
        }
    }
</style>
